// Line Entry — the ONE component every line surface mounts.
//
// `mode="menu"` (default) renders the split button for a lines toolbar (the
// tabular grid, the table view's lines tab, the compact section, the capture
// embeds); `mode="banner"` renders only the `on_header_change` offer, which
// the record editor shows above its forms when the user picks the source
// document in the header. Either way the chooser / picker / "replace the old
// document's lines?" dialogs ride along, and produced lines go to the host's
// `onAppend` for THIS form. `mode="headless"` renders nothing of its own —
// only those dialogs — for a screen that runs a command from code (an 'open'
// action button's follow-up step) through `request`.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { SqlModel } from '../types'
import { useKeyboardShortcuts } from '../keyboard'
import { parseShortcut } from '../actionShortcuts'
import { Z_RECORD_DIALOG } from '../../layers'
import type { LineExecute } from './contract'
import { useLineEntry, type LineEntryNotify, type LineRunOutcome } from './useLineEntry'
import LineEntryMenu from './LineEntryMenu'
import { describeCommand } from './model'
import LineEntryBanner from './LineEntryBanner'
import LinePicker from './LinePicker'
import DocumentChooser from './DocumentChooser'
import LineCriteria, { criteriaRecap } from './LineCriteria'

/** A header field the USER just changed (not a load or a cascade). */
export interface HeaderEdit {
  key: string
  value: unknown
  n: number
}

/** A command to run from code, once per `n`. `select: 'all'` adds every
 *  candidate line without the picker; 'ask' runs the command as a click on it
 *  would (chooser / picker as configured). */
export interface LineCommandRequest {
  command: string
  select: 'all' | 'ask'
  n: number
}

export interface LineEntryBarProps {
  sm: SqlModel
  formNo: string
  execute: LineExecute
  engineKey?: string
  headerValues: Record<string, unknown>
  parentPk?: Record<string, unknown>
  txnId?: string
  rows: Array<Record<string, unknown>>
  onAppend: (rows: Array<Record<string, unknown>>) => void
  onRemove?: (indices: number[]) => void
  notify: LineEntryNotify
  readOnly?: boolean
  /** `empty`: the "choose how lines arrive" cards of an empty lines grid.
   *  `headless`: no surface of its own — runs `request` and shows only the
   *  dialogs that command needs. */
  mode?: 'menu' | 'banner' | 'empty' | 'headless'
  /** Run a command from code (see LineCommandRequest). */
  request?: LineCommandRequest | null
  /** How a `request` ended, reported once per `n`. */
  onRequestDone?: (n: number, outcome: LineRunOutcome) => void
  /** `empty` mode: focus the scan field (offered when the form has quick add). */
  onScan?: () => void
  /** `empty` mode: the grid accepts a paste (Ctrl V in the first cell). */
  pasteHint?: boolean
  headerEdit?: HeaderEdit | null
  compact?: boolean
}

function normKey(k: unknown): string {
  return String(k ?? '').trim().replace(/\s+/g, '_').toLowerCase()
}

/** Shortcut text ("Alt+F") → the keyboard layer's combo ("alt+f"), by the
 *  rules every button's key follows (actionShortcuts.json): a system key or
 *  one the browser owns is no shortcut. */
function comboFor(s: string | undefined): string | null {
  return parseShortcut(s).combo
}

export default function LineEntryBar(props: LineEntryBarProps) {
  const ctl = useLineEntry(props)
  const { le, layout } = ctl
  const mode = props.mode || 'menu'
  const [offer, setOffer] = useState<{ value: unknown; count: number | null } | null>(null)
  const seenEdit = useRef<number | null>(null)
  const seenRequest = useRef<number | null>(null)

  // A command run from code. Waits for the line entry model so a request made
  // while the form is still settling is not lost; reports how it ended.
  useEffect(() => {
    const req = props.request
    if (!req || seenRequest.current === req.n || !le) return
    seenRequest.current = req.n
    const done = props.onRequestDone
    void ctl.run(req.command, { addAll: req.select === 'all' })
      .then((outcome) => done?.(req.n, outcome))
      .catch(() => done?.(req.n, 'failed'))
  }, [props.request, props.onRequestDone, le, ctl])

  // on_header_change: react to the user changing the named header column.
  useEffect(() => {
    const ohc = le?.onHeaderChange
    const ev = props.headerEdit
    if (mode !== 'banner' || !ohc || ohc.do === 'none' || !ev) return
    if (seenEdit.current === ev.n) return
    seenEdit.current = ev.n
    if (normKey(ev.key) !== normKey(ohc.column)) return
    if (ev.value == null || ev.value === '') { setOffer(null); return }
    if (ohc.do === 'run') {
      const first = ohc.commands.find(n => ctl.visibleCommands.some(c => c.name === n))
      if (first) ctl.run(first)
      return
    }
    setOffer({ value: ev.value, count: null })
    // Say how many lines the document holds (one cheap call).
    const firstCmd = ohc.commands.find(n => ctl.visibleCommands.some(c => c.name === n))
      || ctl.visibleCommands[0]?.name
    if (firstCmd) {
      const n = ev.n
      void ctl.countFor(firstCmd, ev.value).then(count => {
        if (seenEdit.current === n) setOffer(o => (o ? { ...o, count } : o))
      })
    }
  }, [props.headerEdit, le, mode, ctl])

  const labelOf = ctl.labelOf
  const describe = useCallback((name: string) => {
    const c = le?.commands.find(x => x.name === name)
    return c ? describeCommand(c, labelOf) : ''
  }, [le, labelOf])
  // "From PO-2026-0143" over the menu when the primary's source names a
  // document this record already holds.
  const primarySrc = ctl.layout?.primary ? le?.sourcesByName[ctl.layout.primary.source] : undefined
  const hdrCol = primarySrc?.documents?.header_column
  const hdrDoc = hdrCol ? props.headerValues?.[normKey(hdrCol)] : undefined
  const menuTitle = hdrDoc != null && hdrDoc !== '' ? `From ${String(hdrDoc)}` : undefined

  useKeyboardShortcuts(
    (mode === 'menu' && ctl.available && !props.readOnly)
      ? ctl.visibleCommands
        .map(c => ({ combo: comboFor(c.shortcut), name: c.name }))
        .filter((s): s is { combo: string; name: string } => !!s.combo)
        .map(s => ({ combo: s.combo, run: () => ctl.run(s.name) }))
      : [],
    { enabled: mode === 'menu' && ctl.available && !props.readOnly },
  )

  if (!le) return null

  const offered = (le.onHeaderChange?.commands.length
    ? le.onHeaderChange.commands
    : ctl.visibleCommands.map(c => c.name))
    .map(n => ctl.visibleCommands.find(c => c.name === n))
    .filter((c): c is NonNullable<typeof c> => !!c)

  const cardKicker: React.CSSProperties = {
    fontWeight: 600, fontSize: 13, letterSpacing: '.04em', textTransform: 'uppercase',
  }
  const card: React.CSSProperties = {
    textAlign: 'left', border: '1px solid #dde3ea', borderRadius: 12, padding: 20, display: 'flex',
    flexDirection: 'column', gap: 10, background: 'var(--color-bg, #fff)', color: 'var(--color-text, #1c2430)',
    fontFamily: 'inherit', fontSize: 15,
  }
  const faceCmd = layout?.primary || ctl.visibleCommands[0]

  return (
    <>
      {mode === 'empty' && ctl.available && !props.readOnly && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16,
          width: '100%', maxWidth: 1040, margin: '0 auto', padding: '24px 16px', boxSizing: 'border-box' }}>
          {faceCmd && (
            <button type="button" onClick={() => ctl.run(faceCmd.name)} disabled={!!ctl.busy}
              style={{ ...card, border: '2px solid var(--color-accent, #0061D0)', background: '#f6f9ff', cursor: 'pointer' }}>
              <span style={{ ...cardKicker, color: 'var(--color-accent, #0061D0)' }}>Fastest</span>
              <span style={{ fontSize: 19, fontWeight: 600 }}>{faceCmd.label}</span>
              <span style={{ color: '#3b4552', lineHeight: 1.5 }}>{describe(faceCmd.name)}</span>
              {faceCmd.shortcut && (
                <span style={{ marginTop: 'auto', fontSize: 13, color: '#5b6675' }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", border: '1px solid #c9d2dc', borderBottomWidth: 2,
                    borderRadius: 4, padding: '0 5px' }}>{faceCmd.shortcut}</span>
                </span>
              )}
            </button>
          )}
          {props.onScan && (
            <button type="button" onClick={props.onScan} style={{ ...card, cursor: 'pointer' }}>
              <span style={{ ...cardKicker, color: '#5b6675' }}>At the dock</span>
              <span style={{ fontSize: 19, fontWeight: 600 }}>Scan or type codes</span>
              <span style={{ color: '#3b4552', lineHeight: 1.5 }}>
                Each scan adds a line, or adds to the count of a line already there. Works with a USB or Bluetooth scanner.
              </span>
              <span style={{ marginTop: 'auto', fontSize: 13, color: '#5b6675' }}>F6 focuses the scan field</span>
            </button>
          )}
          {props.pasteHint && (
            <div style={card}>
              <span style={{ ...cardKicker, color: '#5b6675' }}>From a file</span>
              <span style={{ fontSize: 19, fontWeight: 600 }}>Paste from a spreadsheet</span>
              <span style={{ color: '#3b4552', lineHeight: 1.5 }}>
                Copy rows from Excel and paste into the grid. Each row's own rules fill in the rest.
              </span>
              <span style={{ marginTop: 'auto', fontSize: 13, color: '#5b6675' }}>Ctrl V in the first cell</span>
            </div>
          )}
        </div>
      )}
      {mode === 'menu' && ctl.available && layout && !props.readOnly && (
        <LineEntryMenu layout={layout} busy={ctl.busy} onRun={ctl.run} compact={props.compact}
          describe={describe} menuTitle={menuTitle} />
      )}
      {mode === 'banner' && offer && ctl.available && !props.readOnly && (
        <LineEntryBanner
          document={String(offer.value)}
          count={offer.count}
          noun={(offered[0] && le.sourcesByName[offered[0].source]?.label) || 'lines'}
          commands={offered}
          busy={ctl.busy}
          onRun={(n) => { setOffer(null); ctl.run(n) }}
          onDismiss={() => setOffer(null)}
        />
      )}
      {ctl.chooser && (
        <DocumentChooser
          api={ctl.api} cmd={ctl.chooser}
          noun={le.sourcesByName[ctl.chooser.source]?.label || 'lines'}
          onCancel={ctl.closeChooser} onChoose={ctl.chooseDocuments} onError={ctl.fail}
        />
      )}
      {ctl.criteriaStep && (
        <LineCriteria
          cmd={ctl.criteriaStep.cmd}
          noun={le.sourcesByName[ctl.criteriaStep.cmd.source]?.label || 'lines'}
          criteria={ctl.criteriaStep.step.criteria}
          initial={ctl.criteriaStep.values}
          addsDirectly={ctl.criteriaStep.addAll || ctl.criteriaStep.cmd.picker === 'none'}
          onCancel={ctl.closeCriteria} onSubmit={ctl.submitCriteria}
        />
      )}
      {ctl.picker && (
        <LinePicker
          api={ctl.picker.api} cmd={ctl.picker.cmd} first={ctl.picker.first}
          documents={ctl.picker.documents}
          canChooseMore={!!le.sourcesByName[ctl.picker.cmd.source]?.documents?.choose?.lookup}
          criteriaRecap={ctl.picker.criteria ? criteriaRecap(ctl.picker.criteria.step.criteria,
            ctl.picker.criteria.values) : undefined}
          onChangeCriteria={ctl.picker.criteria ? ctl.changeCriteria : undefined}
          onCancel={ctl.closePicker} onDone={ctl.pickerDone} onError={ctl.fail}
        />
      )}
      {ctl.replaceAsk && (
        <div role="dialog" aria-modal="true" onClick={() => ctl.answerReplace(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: Z_RECORD_DIALOG + 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--color-bg)', color: 'var(--color-text)', borderRadius: 8, padding: 16,
            width: 'min(420px, 100%)', border: '1px solid var(--color-border)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.3)', fontSize: '0.9125rem',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>The document changed</div>
            <div style={{ marginBottom: 14, color: 'var(--color-text-secondary)' }}>
              {ctl.replaceAsk.staleIndices.length} line{ctl.replaceAsk.staleIndices.length === 1 ? '' : 's'} came
              from a document no longer selected. Remove them, or keep them alongside the new lines?
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => ctl.answerReplace(false)}
                style={{ padding: '5px 12px', borderRadius: 4, border: '1px solid var(--color-border)',
                  background: 'var(--color-bg)', color: 'var(--color-text)', cursor: 'pointer' }}>Keep them</button>
              <button type="button" onClick={() => ctl.answerReplace(true)}
                style={{ padding: '5px 12px', borderRadius: 4, border: 'none',
                  background: 'var(--color-accent)', color: '#fff', cursor: 'pointer' }}>Replace them</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
